import { Box, Flex, Heading, Icon, Spinner, Text } from '@chakra-ui/react';
import { Check, X } from 'lucide-react';
import { FunctionComponent, ReactNode } from 'react';
import { STEP_PROGRESS_STATE, TaskState } from '@/constants';

/** Muted helper line inside a task's content. */
export const Hint: FunctionComponent<{ children: string }> = ({ children }) => (
  <Text fontSize="xs" color="marketcast.gray.2">
    {children}
  </Text>
);

/** Error line inside a task's content; announced by screen readers. */
export const ErrorText: FunctionComponent<{ children: string }> = ({ children }) => (
  <Text fontSize="xs" color="red.500" role="alert">
    {children}
  </Text>
);

export interface StepTask {
  id: string;
  title: string;
  state: TaskState;
  meta?: ReactNode;
  content?: ReactNode;
}

type Props = {
  title?: string;
  tasks: StepTask[];
};

const TASK_STATE_STYLES: Record<TaskState, { bg: string; color: string; hasRing?: boolean }> = {
  [STEP_PROGRESS_STATE.UPCOMING]: { bg: 'marketcast.gray.5', color: 'marketcast.gray.2' },
  [STEP_PROGRESS_STATE.ACTIVE]: { bg: 'white', color: 'marketcast.primaryCta.1', hasRing: true },
  [STEP_PROGRESS_STATE.RUNNING]: { bg: 'transparent', color: 'marketcast.primaryCta.1' },
  [STEP_PROGRESS_STATE.COMPLETE]: { bg: 'marketcast.green.1', color: 'white' },
  [STEP_PROGRESS_STATE.ERROR]: { bg: 'red.500', color: 'white' },
};

const TaskIndicator: FunctionComponent<{ state: TaskState; number: number }> = ({
  state,
  number,
}) => {
  const style = TASK_STATE_STYLES[state];

  return (
    <Flex
      w="22px"
      h="22px"
      borderRadius="full"
      align="center"
      justify="center"
      fontSize="11px"
      fontWeight="700"
      bg={style.bg}
      color={style.color}
      border={style.hasRing ? '3px solid' : undefined}
      borderColor={style.hasRing ? 'marketcast.primaryCta.1' : undefined}
    >
      {state === STEP_PROGRESS_STATE.RUNNING && <Spinner size="sm" thickness="3px" />}
      {state === STEP_PROGRESS_STATE.COMPLETE && <Icon as={Check} boxSize={3} />}
      {state === STEP_PROGRESS_STATE.ERROR && <Icon as={X} boxSize={3} />}
      {(state === STEP_PROGRESS_STATE.ACTIVE || state === STEP_PROGRESS_STATE.UPCOMING) && number}
    </Flex>
  );
};

/** Vertical checklist of a step's tasks, kept in view so status never needs a scroll to find. */
export const StepProgress: FunctionComponent<Props> = ({ title = 'Step progress', tasks }) => (
  <Box
    bg="marketcast.base.backgroundAlt"
    borderRadius="12px"
    border="1px solid"
    borderColor="marketcast.gray.5"
    boxShadow="sm"
    p={6}
  >
    <Heading as="h2" fontSize="16px" fontWeight="600" color="marketcast.text.primary" mb={5}>
      {title}
    </Heading>

    <Box as="ol" listStyleType="none" m={0} p={0}>
      {tasks.map((task, index) => {
        const isLast = index === tasks.length - 1;
        const isCurrent =
          task.state === STEP_PROGRESS_STATE.ACTIVE || task.state === STEP_PROGRESS_STATE.RUNNING;

        return (
          <Flex as="li" key={task.id} gap={3} aria-current={isCurrent ? 'step' : undefined}>
            <Flex direction="column" align="center" flexShrink={0}>
              <TaskIndicator state={task.state} number={index + 1} />
              {!isLast && (
                <Box flex={1} w="2px" minH="16px" my={1} bg="marketcast.gray.5" aria-hidden />
              )}
            </Flex>

            <Box flex={1} minW={0} pb={isLast ? 0 : 5}>
              <Flex justify="space-between" align="center" gap={2} minH="22px">
                <Text
                  fontSize="14px"
                  fontWeight="600"
                  color={
                    task.state === STEP_PROGRESS_STATE.UPCOMING
                      ? 'marketcast.gray.2'
                      : 'marketcast.text.primary'
                  }
                >
                  {task.title}
                </Text>
                {task.meta && (
                  <Text fontSize="12px" color="marketcast.gray.2" whiteSpace="nowrap">
                    {task.meta}
                  </Text>
                )}
              </Flex>
              {task.content && <Box mt={2}>{task.content}</Box>}
            </Box>
          </Flex>
        );
      })}
    </Box>
  </Box>
);
